import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { minutesToMillis } from 'helpers'
import { QueryKeys } from '../../key-registry'
import { fetchNewsAttachmentUrlV1 } from './http'

export { fetchNewsAttachmentUrlV1 as fetchNewsAttachmentUrl } from './http'

export interface NewsAttachmentUrls {
  urls: Map<number, string>
  // Every link has either resolved or failed; a failed one stays out of `urls`.
  isSettled: boolean
}

// Links live a day, so half of that is safe to reuse.
const LINK_STALE_TIME = minutesToMillis(12 * 60)

export function useNewsAttachmentUrls(postId: number, attachmentIds: number[]): NewsAttachmentUrls {
  const results = useQueries({
    queries: attachmentIds.map((attachmentId) => ({
      queryKey: QueryKeys.news.attachmentUrl(postId, attachmentId),
      queryFn: () => fetchNewsAttachmentUrlV1(postId, attachmentId),
      staleTime: LINK_STALE_TIME,
      retry: false,
    })),
  })

  // useQueries returns a new array on every render (react-query v4 has no `combine`), so the map is
  // keyed on what it is built from.
  const resolved = results.map((result) => result.data ?? null)
  const resolvedKey = resolved.join('\n')
  const idsKey = attachmentIds.join(',')
  const urls = useMemo(() => {
    const map = new Map<number, string>()
    attachmentIds.forEach((attachmentId, index) => {
      const url = resolved[index]
      if (url !== null) {
        map.set(attachmentId, url)
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, resolvedKey])

  // A cached link may have expired; settled means the current fetch is over, not merely that data exists.
  return { urls, isSettled: results.every((result) => !result.isFetching && (result.isSuccess || result.isError)) }
}
